/**
 * Phase 10 R2 — useUpdateJobLaborOverride
 *
 * Sell-side mutation: write a manual labor-rate override (or clear it) for a
 * single ticket_jobs row.
 *
 * AUTHORITY (FF-A):
 *   The `labor_engine_enabled` flag and `ui_ticket_financials` exposure
 *   are UI gates only. Server authority is enforced by the
 *   `trg_enforce_labor_entitlement` trigger on `ticket_jobs` (migration
 *   `20260501120000_ff_a_labor_entitlement_trigger.sql`), which raises
 *   `insufficient_privilege` if the parent shop is not CATALYST/ENTERPRISE
 *   tier and lacks the `labor_engine` addon. A sessionStorage feature-flag
 *   override cannot bypass that trigger.
 *
 * Key shape: ticket_jobs has a composite PK (ticket_id, job_id). All writes
 * key on both columns.
 *
 * Override semantics:
 *   • setOverride: rate=N → applied=N, overridden=true, resolved preserved
 *   • clearOverride:        → applied=resolved, overridden=false, recompute
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useShopLaborRates } from '@/hooks/labor/useShopLaborRates';
import { computeJobLabor, laborColumnsForUpsert } from '@/lib/labor/computeJobLabor';

interface OverrideArgs {
  ticketId: string;
  jobId: string;
  /** When provided + > 0, sets override. When null/undefined, clears override. */
  overrideRate: number | null;
}

export function useUpdateJobLaborOverride() {
  const qc = useQueryClient();
  const laborEnabled = useFeatureFlag('labor_engine_enabled');
  const financialsEnabled = useExposureFlag('ui_ticket_financials');
  const { data: shopRates } = useShopLaborRates();

  return useMutation({
    mutationFn: async (args: OverrideArgs) => {
      // ── UI gate only. Server authority enforced by RLS trigger
      //    (trg_enforce_labor_entitlement, FF-A). ─────────────────────
      if (!laborEnabled || !financialsEnabled) {
        toast({
          title: 'Labor financials disabled',
          description: 'Labor financials are disabled for this shop.',
          variant: 'destructive',
        });
        return { skipped: true as const };
      }
      if (!shopRates) {
        toast({
          title: 'Shop labor rates not loaded',
          description: 'Try again in a moment.',
          variant: 'destructive',
        });
        return { skipped: true as const };
      }

      // Re-read the row so we recompute against current effective minutes + category.
      const { data: row, error: rowErr } = await (supabase as any)
        .from('ticket_jobs')
        .select(
          `minutes_override, sold_minutes,
           jobs:job_id(standard_minutes, category)`,
        )
        .eq('ticket_id', args.ticketId)
        .eq('job_id', args.jobId)
        .single();
      if (rowErr) throw rowErr;

      const effective =
        row.minutes_override ?? row.jobs?.standard_minutes ?? row.sold_minutes ?? 0;

      const computed = computeJobLabor({
        effectiveMinutes: effective,
        jobCategory: row.jobs?.category ?? null,
        shopRates,
        override:
          args.overrideRate != null && args.overrideRate > 0
            ? { rate: Number(args.overrideRate) }
            : null,
      });

      const { error: updErr } = await (supabase as any)
        .from('ticket_jobs')
        .update(laborColumnsForUpsert(computed))
        .eq('ticket_id', args.ticketId)
        .eq('job_id', args.jobId);
      if (updErr) throw updErr;

      return { skipped: false as const, computed };
    },
    onSuccess: (res, args) => {
      if (res?.skipped) return;
      qc.invalidateQueries({ queryKey: ['ticket-labor-lines', args.ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket-jobs-for-parts', args.ticketId] });
      toast({
        title: args.overrideRate ? 'Labor rate overridden' : 'Override cleared',
        description: args.overrideRate
          ? `Applied $${Number(args.overrideRate).toFixed(2)}/hr.`
          : 'Reverted to resolved rate.',
      });
    },
    onError: (e: any) => {
      toast({
        title: 'Failed to update labor rate',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}
